import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FileDown, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const MONTHS_NL = [
    "januari", "februari", "maart", "april", "mei", "juni",
    "juli", "augustus", "september", "oktober", "november", "december",
];

/** MM-only dialog to pick a month/year and download the PDF report. */
export default function MMMonthlyReportDialog({ open, onOpenChange }) {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [downloading, setDownloading] = useState(false);

    // Year choices: current year + 4 prior
    const currentYear = now.getFullYear();
    const yearChoices = Array.from({ length: 5 }, (_, i) => currentYear - i);

    const download = async () => {
        setDownloading(true);
        try {
            const res = await api.get(`/mm/monthly-report/${year}/${month}`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Maandrapport_${year}-${String(month).padStart(2, "0")}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 4000);
            toast.success("Maandrapport gedownload");
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Download mislukt");
        }
        setDownloading(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#FFFFFF] border-zinc-200 text-zinc-950 max-w-md" data-testid="mm-monthly-report-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display font-bold text-2xl tracking-tight flex items-center gap-2">
                        <FileDown className="h-5 w-5 text-[#7C3AED]" />
                        Maandrapport
                    </DialogTitle>
                    <p className="text-sm text-zinc-500">
                        Overzicht van alle <strong className="text-zinc-950">opgeleverde</strong> projecten van uw makelaars in de gekozen maand —
                        bedoeld voor interne kostenverdeling.
                    </p>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-3 py-3">
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Maand</Label>
                        <select
                            value={month}
                            onChange={(e) => setMonth(parseInt(e.target.value))}
                            className="w-full bg-[#F4F4F5] border border-zinc-200 rounded-sm h-11 px-3 text-sm text-zinc-950 focus:outline-none focus:border-[#7C3AED]"
                            data-testid="mm-report-month-select"
                        >
                            {MONTHS_NL.map((m, i) => (
                                <option key={i} value={i + 1}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Jaar</Label>
                        <select
                            value={year}
                            onChange={(e) => setYear(parseInt(e.target.value))}
                            className="w-full bg-[#F4F4F5] border border-zinc-200 rounded-sm h-11 px-3 text-sm text-zinc-950 focus:outline-none focus:border-[#7C3AED]"
                            data-testid="mm-report-year-select"
                        >
                            {yearChoices.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={downloading}
                        className="text-zinc-500 hover:text-zinc-950"
                        data-testid="mm-report-cancel"
                    >
                        Annuleren
                    </Button>
                    <Button
                        onClick={download}
                        disabled={downloading}
                        className="bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-bold rounded-sm active:scale-[0.98]"
                        data-testid="mm-report-download"
                    >
                        {downloading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Genereren…</> : <><FileDown className="h-4 w-4 mr-2" /> Download PDF</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
